<template>
  <BaseModal
    modal-id="location-confirm-modal"
    title="位置設定確認"
    cancel-text="キャンセル"
    confirm-text="OK"
    v-model="internalVisible"
    ref="baseModal"
    @cancel="handleCancel"
    @confirm="handleConfirm"
  >
    <div class="location-confirm-content">
      <p class="confirm-message">
        {{ facilityName }}の位置情報を以下の座標に変更しますか？
      </p>

      <div class="coordinate-info">
        <div class="coordinate-row">
          <span class="coordinate-label">緯度:</span>
          <span class="coordinate-value">{{ formatCoordinate(newPosition.lat) }}</span>
        </div>
        <div class="coordinate-row">
          <span class="coordinate-label">経度:</span>
          <span class="coordinate-value">{{ formatCoordinate(newPosition.lng) }}</span>
        </div>
      </div>

      <div class="position-change-info" v-if="showDistance">
        <p class="change-text">
          元の位置から約 {{ distanceText }} 移動します
        </p>
      </div>
    </div>
  </BaseModal>
</template>

<script setup>
import { ref, computed } from 'vue'
import BaseModal from '../molecules/BaseModal.vue'

/**
 * LocationConfirmModal - 位置設定確認モーダル（Organismsレベルコンポーネント）
 *
 * アトミックデザインに準拠した位置情報変更確認専用のモーダルコンポーネントです。
 * BaseModal（Molecules）を基盤とし、ドラッグによる位置変更の確認機能を提供します。
 * 設計方針に従い、特定の業務機能（位置情報変更確認）に特化したUIコンポーネントとして設計されています。
 *
 * アトミックデザイン分類: Organisms（有機体）
 * - BaseModal（分子）を基盤とした業務特化型モーダルコンポーネント
 * - 位置情報変更確認という特定の業務機能に特化
 * - 座標情報表示と移動距離計算を統合した複合的なUIコンポーネント
 *
 * 責務:
 * - 位置変更確認のモーダル表示
 * - 新しい座標の表示
 * - 移動距離の計算と表示
 * - ユーザーの選択（OK/キャンセル）の受付
 * - 親コンポーネントへの確認イベント通知
 * - BaseModalを通じたモーダルの表示/非表示制御
 *
 * 外部インターフェース:
 * -  Props: facilityName, originalPosition, newPosition
 * - showModal(): 外部からモーダルを表示するメソッド
 * - hideModal(): 外部からモーダルを非表示にするメソッド
 * - confirm イベント: ユーザーがOKを選択した際に発火
 * - cancel イベント: ユーザーがキャンセルを選択した際に発火
 *
 * 使用例:
 * <LocationConfirmModal
 *   ref="locationConfirmModal"
 *   :facility-name="currentFacility.name"
 *   :original-position="originalCoords"
 *   :new-position="newCoords"
 *   @confirm="handleLocationConfirm"
 *   @cancel="handleLocationCancel"
 * />
 * this.$refs.locationConfirmModal.showModal()
 */

const props = defineProps({
  /**
   * 施設名
   */
  facilityName: {
    type: String,
    default: '施設'
  },

  /**
   * 元の位置座標
   * { lat: number, lng: number } 形式
   */
  originalPosition: {
    type: Object,
    default: () => ({ lat: 0, lng: 0 })
  },

  /**
   * 新しい位置座標
   * { lat: number, lng: number } 形式
   */
  newPosition: {
    type: Object,
    default: () => ({ lat: 0, lng: 0 })
  },

  /**
   * モーダルの表示状態（Vue 2互換）
   */
  value: {
    type: Boolean,
    default: false
  },

  /**
   * モーダルの表示状態（Vue 3 v-model）
   */
  modelValue: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['input', 'update:modelValue', 'confirm', 'cancel'])

const baseModal = ref(null)

/**
 * 移動距離を表示するかどうか
 */
const showDistance = computed(() => {
  return props.originalPosition.lat !== 0 && props.originalPosition.lng !== 0
})

/**
 * 移動距離のテキスト
 */
const distanceText = computed(() => {
  if (!showDistance.value) return ''

  const distance = calculateDistance(
    props.originalPosition,
    props.newPosition
  )

  // distanceがnullまたはNaNの場合は0として扱う
  const safeDistance = (distance != null && !isNaN(distance)) ? distance : 0

  if (safeDistance < 1) {
    return `${Math.round(safeDistance * 1000)}m`
  } else {
    return `${safeDistance.toFixed(1)}km`
  }
})

/**
 * 内部的なモーダル表示状態
 * v-modelとの双方向バインディング用
 */
const internalVisible = computed({
  get: () => props.modelValue !== undefined ? props.modelValue : props.value,
  set: (visible) => {
    // Vue 2互換のinputイベント
    emit('input', visible)
    // Vue 3のv-modelイベント
    emit('update:modelValue', visible)
  }
})

/**
 * モーダルを表示します
 */
const showModal = () => {
  baseModal.value.showModal()
}

/**
 * モーダルを非表示にします
 */
const hideModal = () => {
  baseModal.value.hideModal()
}

/**
 * 座標を見やすい形式でフォーマット
 */
const formatCoordinate = (value) => {
  return (value != null ? value : 0).toFixed(6)
}

/**
 * 2点間の距離を計算（Haversine公式）
 */
const calculateDistance = (pos1, pos2) => {
  const R = 6371 // 地球の半径（km）
  const dLat = toRadian(pos2.lat - pos1.lat)
  const dLng = toRadian(pos2.lng - pos1.lng)

  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRadian(pos1.lat)) * Math.cos(toRadian(pos2.lat)) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return R * c
}

/**
 * 度をラジアンに変換
 */
const toRadian = (degree) => {
  return degree * (Math.PI / 180)
}

/**
 * キャンセル時の処理
 */
const handleCancel = () => {
  emit('cancel')
  // モーダルを閉じる
  internalVisible.value = false
}

/**
 * 確認時の処理
 */
const handleConfirm = () => {
  // 引数なしでconfirmイベントを発火（親コンポーネントがpendingLocationChangeを持っているため）
  emit('confirm')
  // モーダルを閉じる
  internalVisible.value = false
}

defineExpose({
  showModal,
  hideModal
})
</script>

<style lang="scss" scoped>
.location-confirm-content {
  text-align: center;
}

.confirm-message {
  margin-bottom: 1.5rem;
  font-size: 1rem;
  color: #333;
}

.coordinate-info {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 1rem;
  margin-bottom: 1rem;
}

.coordinate-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.coordinate-label {
  font-weight: 600;
  color: #495057;
}

.coordinate-value {
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  color: #007bff;
  font-weight: 500;
}

.position-change-info {
  padding: 0.75rem;
  background-color: #e3f2fd;
  border: 1px solid #bbdefb;
  border-radius: 0.375rem;
}

.change-text {
  margin: 0;
  font-size: 0.875rem;
  color: #1976d2;
  font-weight: 500;
}

/* レスポンシブ対応 */
@media (max-width: 576px) {
  .coordinate-row {
    flex-direction: column;
    text-align: left;
  }

  .coordinate-label {
    margin-bottom: 0.25rem;
  }
}
</style>
